import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'

type TrialContextValue = {
    isOpen: boolean
    source: string
    openCount: number
    openTrial: (source: string) => void
    closeTrial: () => void
}

interface TrialProviderProps {
    children: ReactNode
}

const TrialContext = createContext<TrialContextValue | null>(null)

// Every "Get started free" button on the site opens the same signup dialog through this context.
export const TrialProvider: FC<TrialProviderProps> = ({ children }): ReactNode => {
    const [isOpen, setIsOpen] = useState(false)
    const [source, setSource] = useState('nav')
    const [openCount, setOpenCount] = useState(0)

    const openTrial = useCallback((nextSource: string) => {
        setSource(nextSource)
        setOpenCount((count) => count + 1)
        setIsOpen(true)
    }, [])

    const closeTrial = useCallback(() => {
        setIsOpen(false)
    }, [])

    const value = useMemo(
        () => ({ isOpen, source, openCount, openTrial, closeTrial }),
        [isOpen, source, openCount, openTrial, closeTrial]
    )

    return (
        <TrialContext.Provider value={value}>
            {children}
        </TrialContext.Provider>
    )
}

export const useTrial = (): TrialContextValue => {
    const context = useContext(TrialContext)

    if (!context) {
        throw new Error('useTrial must be used inside TrialProvider')
    }

    return context
}
